import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, Send, ArrowLeft, Inbox, Users, User, School, 
  Check, CheckCheck, MessageSquare, Search 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Messages() {
  const { user, getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();
  
  const [inbox, setInbox] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Compose form
  const [recipientType, setRecipientType] = useState('all');
  const [recipientId, setRecipientId] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  
  // For admin individual messaging
  const [allUsers, setAllUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  
  // For class teacher
  const [myClasses, setMyClasses] = useState([]);

  useEffect(() => {
    fetchMessages();
    if (user?.role === 'admin') {
      fetchUsersAndClasses();
    }
    if (user?.role === 'teacher') {
      fetchMyClasses();
    }
  }, []);

  const fetchMessages = async () => {
    try {
      const inboxRes = await axios.get(`${API}/messages/inbox`, { headers: getAuthHeaders() });
      setInbox(inboxRes.data);
      
      if (user?.role === 'admin') {
        const sentRes = await axios.get(`${API}/admin/messages/sent`, { headers: getAuthHeaders() });
        setSentMessages(sentRes.data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersAndClasses = async () => {
    try {
      const [usersRes, classesRes] = await Promise.all([
        axios.get(`${API}/admin/users-for-messaging`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/classes`, { headers: getAuthHeaders() }),
      ]);
      setAllUsers(usersRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      console.error('Error fetching users/classes:', error);
    }
  };

  const fetchMyClasses = async () => {
    try {
      const response = await axios.get(`${API}/teacher/my-classes`, { headers: getAuthHeaders() });
      setMyClasses(response.data);
    } catch (error) {
      console.error('Error fetching my classes:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error('Тақырып пен мәтін толтырыңыз');
      return;
    }
    
    setSending(true);
    try {
      if (user?.role === 'admin') {
        await axios.post(`${API}/admin/messages`, {
          recipient_type: recipientType,
          recipient_id: recipientType === 'individual' || recipientType === 'class' ? recipientId : null,
          subject,
          content
        }, { headers: getAuthHeaders() });
      } else if (user?.role === 'teacher' && recipientType === 'class') {
        await axios.post(`${API}/teacher/messages/to-class/${recipientId}`, {
          subject,
          content
        }, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/messages/to-admin`, {
          subject,
          content
        }, { headers: getAuthHeaders() });
      }
      
      toast.success('Хабарлама жіберілді');
      setComposeOpen(false);
      setSubject('');
      setContent('');
      setRecipientType('all');
      setRecipientId('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    } finally {
      setSending(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, { headers: getAuthHeaders() });
      setInbox(inbox.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      markAsRead(message.id);
    }
  };

  const getRecipientLabel = (type) => {
    switch (type) {
      case 'all': return 'Барлығына';
      case 'teachers': return 'Мұғалімдерге';
      case 'students': return 'Оқушыларға';
      case 'class': return 'Сыныпқа';
      case 'individual': return 'Жеке';
      case 'admin': return 'Админге';
      default: return type;
    }
  };

  const filteredInbox = inbox.filter(msg => 
    msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.sender_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = inbox.filter(msg => !msg.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-lg text-muted">Жүктелуде...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white py-4 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(-1)}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Артқа
              </Button>
              <div className="flex items-center space-x-2">
                <Mail className="h-6 w-6" />
                <h1 className="text-xl font-bold">Хабарламалар</h1>
              </div>
            </div>
            
            <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-primary hover:bg-white/90">
                  <Send className="w-4 h-4 mr-2" />
                  Жаңа хабарлама
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Жаңа хабарлама жіберу</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Recipient Selection - Admin Only */}
                  {user?.role === 'admin' && (
                    <div>
                      <Label>Кімге</Label>
                      <Select value={recipientType} onValueChange={(v) => { setRecipientType(v); setRecipientId(''); }}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            <span className="flex items-center gap-2">
                              <Users className="w-4 h-4" /> Барлығына
                            </span>
                          </SelectItem>
                          <SelectItem value="teachers">
                            <span className="flex items-center gap-2">
                              <User className="w-4 h-4" /> Барлық мұғалімдерге
                            </span>
                          </SelectItem>
                          <SelectItem value="students">
                            <span className="flex items-center gap-2">
                              <User className="w-4 h-4" /> Барлық оқушыларға
                            </span>
                          </SelectItem>
                          <SelectItem value="class">
                            <span className="flex items-center gap-2">
                              <School className="w-4 h-4" /> Сыныпқа
                            </span>
                          </SelectItem>
                          <SelectItem value="individual">
                            <span className="flex items-center gap-2">
                              <User className="w-4 h-4" /> Жеке пайдаланушыға
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Class Selection for Admin */}
                  {user?.role === 'admin' && recipientType === 'class' && (
                    <div>
                      <Label>Сынып таңдаңыз</Label>
                      <Select value={recipientId} onValueChange={setRecipientId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Сынып таңдаңыз" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map(cls => (
                            <SelectItem key={cls.class_id} value={cls.class_id}>
                              {cls.class_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Individual User Selection for Admin */}
                  {user?.role === 'admin' && recipientType === 'individual' && (
                    <div>
                      <Label>Пайдаланушы таңдаңыз</Label>
                      <Select value={recipientId} onValueChange={setRecipientId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Пайдаланушы таңдаңыз" />
                        </SelectTrigger>
                        <SelectContent>
                          {allUsers.filter(u => u.id !== user.id).map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.full_name} ({u.role === 'teacher' ? 'Мұғалім' : u.role === 'student' ? 'Оқушы' : 'Admin'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Teacher: To Admin or To Class */}
                  {user?.role === 'teacher' && (
                    <div>
                      <Label>Кімге</Label>
                      <Select value={recipientType} onValueChange={(v) => { setRecipientType(v); setRecipientId(''); }}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin'e</SelectItem>
                          {myClasses.length > 0 && (
                            <SelectItem value="class">Сыныбыма</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Teacher Class Selection */}
                  {user?.role === 'teacher' && recipientType === 'class' && myClasses.length > 0 && (
                    <div>
                      <Label>Сынып таңдаңыз</Label>
                      <Select value={recipientId} onValueChange={setRecipientId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Сынып таңдаңыз" />
                        </SelectTrigger>
                        <SelectContent>
                          {myClasses.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>
                              {cls.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Student: Only to Admin */}
                  {user?.role === 'student' && (
                    <div className="p-3 bg-secondary/30 rounded-lg text-sm">
                      <span className="text-muted">Кімге:</span> Admin'e
                    </div>
                  )}
                  
                  <div>
                    <Label>Тақырып</Label>
                    <Input 
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Хабарлама тақырыбы"
                    />
                  </div>
                  
                  <div>
                    <Label>Мәтін</Label>
                    <Textarea 
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Хабарлама мәтіні..."
                      rows={5}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleSendMessage} 
                    disabled={sending || !subject.trim() || !content.trim()}
                    className="w-full"
                  >
                    {sending ? 'Жіберілуде...' : 'Жіберу'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs defaultValue="inbox" className="space-y-4">
          <TabsList>
            <TabsTrigger value="inbox" className="relative">
              <Inbox className="w-4 h-4 mr-2" />
              Кіріс ({inbox.length})
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="sent">
                <Send className="w-4 h-4 mr-2" />
                Жіберілген ({sentMessages.length})
              </TabsTrigger>
            )}
          </TabsList>

          {/* Inbox Tab */}
          <TabsContent value="inbox">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle>Кіріс хабарламалар</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <Input 
                      placeholder="Іздеу..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredInbox.length === 0 ? (
                  <div className="text-center py-12 text-muted">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Хабарлама жоқ</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredInbox.map((msg) => (
                      <div
                        key={msg.id}
                        onClick={() => openMessage(msg)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                          msg.is_read ? 'bg-background' : 'bg-primary/5 border-primary/20'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-semibold ${!msg.is_read ? 'text-primary' : ''}`}>
                                {msg.sender_name}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-secondary rounded">
                                {msg.sender_role === 'admin' ? 'Admin' : msg.sender_role === 'teacher' ? 'Мұғалім' : 'Оқушы'}
                              </span>
                            </div>
                            <p className={`font-medium truncate ${!msg.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {msg.subject}
                            </p>
                            <p className="text-sm text-muted truncate mt-1">{msg.content}</p>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-xs text-muted">
                              {format(new Date(msg.created_at), 'dd.MM.yyyy HH:mm')}
                            </p>
                            {msg.is_read ? (
                              <CheckCheck className="w-4 h-4 text-green-500 ml-auto mt-1" />
                            ) : (
                              <Check className="w-4 h-4 text-muted ml-auto mt-1" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sent Tab (Admin Only) */}
          {user?.role === 'admin' && (
            <TabsContent value="sent">
              <Card>
                <CardHeader>
                  <CardTitle>Жіберілген хабарламалар</CardTitle>
                </CardHeader>
                <CardContent>
                  {sentMessages.length === 0 ? (
                    <div className="text-center py-12 text-muted">
                      <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Жіберілген хабарлама жоқ</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sentMessages.map((msg) => (
                        <div
                          key={msg.id}
                          onClick={() => setSelectedMessage(msg)}
                          className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">
                                  {getRecipientLabel(msg.recipient_type)}
                                  {msg.recipient_name && `: ${msg.recipient_name}`}
                                </span>
                              </div>
                              <p className="font-medium truncate">{msg.subject}</p>
                              <p className="text-sm text-muted truncate mt-1">{msg.content}</p>
                            </div>
                            <p className="text-xs text-muted ml-4">
                              {format(new Date(msg.created_at), 'dd.MM.yyyy HH:mm')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="max-w-lg">
          {selectedMessage && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedMessage.subject}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{selectedMessage.sender_name}</span>
                    <span className="text-xs px-2 py-0.5 bg-secondary rounded">
                      {selectedMessage.sender_role === 'admin' ? 'Admin' : 
                       selectedMessage.sender_role === 'teacher' ? 'Мұғалім' : 'Оқушы'}
                    </span>
                  </div>
                  <span className="text-muted">
                    {format(new Date(selectedMessage.created_at), 'dd.MM.yyyy HH:mm')}
                  </span>
                </div>
                
                {selectedMessage.recipient_type !== 'admin' && (
                  <div className="text-sm text-muted">
                    Кімге: {getRecipientLabel(selectedMessage.recipient_type)}
                    {selectedMessage.recipient_name && ` - ${selectedMessage.recipient_name}`}
                  </div>
                )}
                
                <div className="bg-secondary/30 rounded-lg p-4 whitespace-pre-wrap">
                  {selectedMessage.content}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
